/**
 * Voice Memo Transcript Panel Component
 * Displays transcript content in SecondaryPanel
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { SecondaryPanelContent, SecondaryPanelOpenButton } from '@/components/layout/secondary-panel-content';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import { fetchWithAuth } from '@/lib/api';
import { useInternalMarkdownLinkClick } from '@/lib/internal-link-navigation';
import type { VoiceMemo } from '@/lib/api';

interface VoiceMemoTranscriptPanelProps {
  memo: VoiceMemo;
  onClose: () => void;
  onOpenInEditor?: (filePath: string) => void;
}

export function VoiceMemoTranscriptPanel({
  memo,
  onClose,
  onOpenInEditor
}: VoiceMemoTranscriptPanelProps) {
  const [transcriptContent, setTranscriptContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onInternalLinkClick = useInternalMarkdownLinkClick(
    memo.transcript?.path ?? undefined
  );

  useEffect(() => {
    const loadTranscript = async () => {
      if (!memo.transcript?.path) {
        setError('Transcript path not available');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Extract the memo ID from the path (filename without extension)
        let memoId = memo.id;

        // If ID isn't set, try to extract from transcript path
        if (!memoId) {
          const pathMatch = memo.transcript.path.match(/\/([^/]+)\.md$/);
          if (pathMatch) {
            memoId = pathMatch[1];
          }
        }

        // Fetch transcript content using dedicated voice memo endpoint
        const response = await fetchWithAuth(`/api/voice-memos/${encodeURIComponent(memoId)}/transcript`);

        if (!response.ok) {
          throw new Error('Failed to load transcript');
        }

        const data = await response.json();
        setTranscriptContent(data.content || '');
      } catch (err) {
        console.error('Failed to load transcript:', err);
        setError(err instanceof Error ? err.message : 'Failed to load transcript');
      } finally {
        setIsLoading(false);
      }
    };

    loadTranscript();
  }, [memo.id, memo.transcript?.path]);

  const handleOpenInEditor = async () => {
    if (!onOpenInEditor) return;

    // Strategy: Fetch transcript to get the actual file path from API response
    let memoId = memo.id;
    if (!memoId && memo.filename) {
      memoId = memo.filename.replace(/\.(m4a|m4b|wav|mp3)$/i, '');
    }

    if (!memoId) {
      console.error('Could not determine memo ID for transcript:', memo);
      return;
    }

    try {
      // Fetch transcript to get the actual path from the API
      const response = await fetchWithAuth(`/api/voice-memos/${encodeURIComponent(memoId)}/transcript`);

      if (response.ok) {
        const data = await response.json();
        // Use the path from the API response if available
        if (data.path) {
          // Ensure path is in the correct format (relative from project root)
          let transcriptPath = data.path;
          // Normalize to ensure it starts with inbox/voice-memos
          if (!transcriptPath.startsWith('inbox/voice-memos/')) {
            // Extract just the filename if path is absolute or malformed
            const filename = transcriptPath.split('/').pop() || data.filename || `${memoId}.md`;
            transcriptPath = `inbox/voice-memos/${filename}`;
          }
          onOpenInEditor(transcriptPath);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch transcript path from API, using fallback:', err);
    }

    // Fallback: Construct path using memo ID
    const transcriptPath = `inbox/voice-memos/${memoId}.md`;
    onOpenInEditor(transcriptPath);
  };

  return (
    <SecondaryPanelContent
      title={`Transcript: ${memo.filename.replace(/\.(m4a|wav|m4b)$/, '')}`}
      onClose={onClose}
      loading={isLoading}
      error={error}
      headerActions={
        memo.transcript?.path ? (
          <SecondaryPanelOpenButton
            onClick={handleOpenInEditor}
          />
        ) : undefined
      }
    >
      {transcriptContent ? (
        <div className="p-6">
          <div
            style={{
              animation: 'slideDownStagger 0.4s ease-out 0s both'
            }}
          >
            <MarkdownPreview
              content={transcriptContent}
              className="max-w-none"
              compact
              onInternalLinkClick={onInternalLinkClick}
            />
          </div>
        </div>
      ) : null}
    </SecondaryPanelContent>
  );
}

